import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';

import { STATIC_WIZARD_PARAMS } from '../../../shared/constants';

import { baseTrackPageView, DEQ_PAGE_TYPES } from '../../../utils/deq';
import { confirmWizardParam } from '../../layouts/wizard-layout/WizardLayout.utils';
import SEO from '../../utils/SEO';

import colors from '../../../styles/colors';

import { getRecaptchaSiteKey } from '../../../services/re-captcha.service';
import BaseLayout from '../../layouts/BaseLayout';
import WizardLayout from '../../layouts/wizard-layout/WizardLayout';

import { WizardPageProps } from './WizardPage.types';

const trackingConfig = {
  pageName: 'wizard',
  eventName: 'form step',
  leadType: 'lead-hard',
};

const WizardPage = ({ data, pageContext, location }: WizardPageProps) => {
  const pageData = data?.contentfulPageWizard;
  const { layoutType } = pageContext;
  const {
    id,
    version,
    title,
    body,
    steps,
    resultPage,
    showResultTitles,
    contactForm,
    metadataTitle,
    wizardName = '',
    navigationType,
    enableReset,
    parameterEmail,
    wizardIntro,
  } = pageData;
  const trackingName = pageData.trackingName || wizardName;
  const trackingLeadType = pageData.trackingLeadType || trackingConfig.leadType;
  const [customEmail, setCustomEmail] = useState<string>();
  const [isUserFromOpenDomain, setIsUserFromOpenDomain] = useState(false);
  const headerProps = {
    title,
    resultTitle: resultPage.pageName,
    label: wizardName,
  };
  const introProps = wizardIntro && {
    body: wizardIntro.body?.childMarkdownRemark.rawMarkdownBody || '',
    buttonLabel: wizardIntro.nextStartButtonLabel,
  };
  const formProps = contactForm && {
    form: contactForm,
    slug: pageData.slug,
    customEmail,
  };
  const resultProps = {
    name: wizardName,
    header: resultPage.header,
    body: resultPage.body,
    noResultCards: resultPage.headerCards,
    showResultTitles,
    enableCustomResultMail: !formProps,
  };
  const metaDataProps = {
    id,
    version,
    name: wizardName,
    trackingName,
    leadType: trackingLeadType,
    eventName: trackingConfig.eventName,
  };

  const checkIfUserIsFromOpenDomain = () => {
    if (confirmWizardParam(STATIC_WIZARD_PARAMS.openDomain)) {
      setIsUserFromOpenDomain(true);
    }
  };

  useEffect(() => {
    checkIfUserIsFromOpenDomain();
    const type = new URLSearchParams(location.search).get('type');
    if (parameterEmail) {
      const foundParameter = parameterEmail.find(
        ({ parameterName }) =>
          parameterName.toLowerCase() === type?.toLowerCase()
      );

      if (foundParameter) {
        setCustomEmail(foundParameter.value);
      }
    }
  }, [location]);

  useEffect(() => {
    baseTrackPageView('wizard', DEQ_PAGE_TYPES.action);
  }, []);

  return (
    <BaseLayout
      layoutType={layoutType}
      backgroundColor={isUserFromOpenDomain ? colors.abnNeutral50 : undefined}
    >
      <SEO title={metadataTitle} />
      <WizardLayout
        name={wizardName}
        navigationType={navigationType}
        enableReset={!!enableReset}
        header={headerProps}
        steps={body?.steps || steps}
        intro={introProps}
        form={formProps}
        results={resultProps}
        metaData={metaDataProps}
        pageSlug={pageData.slug}
        parentPage={pageData.parentPage}
        isIframed={isUserFromOpenDomain}
      />
    </BaseLayout>
  );
};

export const data = graphql`
  query wizards($slug: String) {
    contentfulPageWizard(slug: { eq: $slug }) {
      id
      slug
      version
      metadataTitle
      wizardName
      navigationType
      enableReset
      trackingName
      trackingLeadType
      parameterEmail {
        parameterName
        value
      }
      title
      body {
        ...WizardComponent
      }
      steps {
        ...WizardSteps
      }
      wizardIntro {
        id
        title
        body {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
        nextStartButtonLabel
      }
      contactForm {
        ... on ContentfulFormContactForm {
          ...ContactForm
        }
      }
      parentPage {
        ...ParentPageGeneric
        ...ParentPageOverview
      }
      resultPage {
        header {
          ...Card
        }
        headerCards {
          ...Card
        }
        pageName
        metadataTitle
        pageType
        body {
          __typename
          ... on ContentfulTabWrapper {
            position
            tabs {
              ...Tab
              body {
                ...Card
              }
            }
          }
          ... on ContentfulCompCard {
            ...Card
          }
          ... on ContentfulCompCardWrapper {
            ...CardWrapper
          }
        }
        parentPage {
          ...ParentPageGeneric
          ...ParentPageOverview
        }
      }
      showResultTitles
    }
  }
`;

export default WizardPage;

export function Head() {
  return (
    <script
      async
      src={`https://www.google.com/recaptcha/api.js?render=${getRecaptchaSiteKey()}`}
    />
  );
}
